import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { FileText, Loader2, Upload, CheckCircle, AlertTriangle, Circle, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusMeta = (item) => {
  if (item.status === 'expired') return { icon: AlertTriangle, color: 'text-red-600', text: 'Expired' };
  if (item.status === 'approved') return { icon: CheckCircle, color: 'text-green-600', text: 'Approved' };
  if (item.uploaded) return { icon: CheckCircle, color: 'text-blue-600', text: item.status || 'Uploaded' };
  return { icon: Circle, color: 'text-gray-300', text: 'Missing' };
};

const DriverDocumentCenterPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [data, setData] = useState({ documents: [], summary: {} });
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/driver-document-center`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 403) { setAvailable(false); return; }
      if (!res.ok) throw new Error();
      setData(await res.json()); setAvailable(true);
    } catch { /* keep */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const upload = async (docType, file) => {
    if (!file) return;
    setBusy(docType);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/documents/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (res.ok) await load();
    } finally { setBusy(null); }
  };

  const download = async (id) => {
    const token = localStorage.getItem('token');
    const res = await fetch(`${API_URL}/api/documents/${id}/download`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { const d = await res.json(); if (d.download_url) window.open(d.download_url, '_blank'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="driver-document-center-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-blue-600" /> Document Center
          </h1>
          <p className="text-gray-600 mt-1">View, upload, replace, and track your required documents.</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="ddc-loading"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="ddc-unavailable">
            <p className="text-gray-700">Document Center is not currently available.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3" data-testid="ddc-summary">
              <div className="rounded-lg border border-gray-200 p-4"><p className="text-2xl font-bold">{data.summary.uploaded ?? 0}</p><p className="text-xs text-gray-500 uppercase">Uploaded</p></div>
              <div className="rounded-lg border border-gray-200 p-4"><p className="text-2xl font-bold text-amber-600">{data.summary.missing ?? 0}</p><p className="text-xs text-gray-500 uppercase">Missing</p></div>
              <div className="rounded-lg border border-gray-200 p-4"><p className="text-2xl font-bold text-red-600">{data.summary.expired ?? 0}</p><p className="text-xs text-gray-500 uppercase">Expired</p></div>
            </div>

            <div className="space-y-2" data-testid="ddc-list">
              {data.documents.map((item) => {
                const m = statusMeta(item); const Icon = m.icon;
                return (
                  <div key={item.document_type} data-testid={`ddc-item-${item.document_type}`}
                       className="flex items-center gap-3 rounded-lg border border-gray-200 p-4">
                    <Icon className={`w-5 h-5 ${m.color}`} />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{item.label}{item.required && <span className="text-red-500 ml-1">*</span>}</p>
                      <p className={`text-xs ${m.color}`}>{m.text}{item.filename ? ` · ${item.filename}` : ''}{item.expiration_date ? ` · exp ${String(item.expiration_date).slice(0,10)}` : ''}</p>
                    </div>
                    {item.document_id && (
                      <button type="button" onClick={() => download(item.document_id)} data-testid={`ddc-download-${item.document_type}`} className="text-blue-600 text-sm inline-flex items-center gap-1 hover:underline"><Download className="w-4 h-4" /> View</button>
                    )}
                    <label className="text-sm inline-flex items-center gap-1 text-gray-700 cursor-pointer hover:text-blue-600" data-testid={`ddc-upload-${item.document_type}`}>
                      {busy === item.document_type ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                      {item.uploaded ? 'Replace' : 'Upload'}
                      <input type="file" className="hidden" onChange={(e) => upload(item.document_type, e.target.files[0])} />
                    </label>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DriverDocumentCenterPage;
